/**
 * StatsCards Component
 * Quick stats: spesa totale ultimi 12 mesi, media mensile, trend %
 */

import React, { useMemo } from 'react';

function StatsCards({ bollette }) {
  const stats = useMemo(() => {
    const now = new Date();
    const last12MonthsStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const last6MonthsStart = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const prev6MonthsStart = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const prev6MonthsEnd = new Date(now.getFullYear(), now.getMonth() - 6, 0);

    // Last 12 months
    const last12Months = bollette.filter(b => 
      new Date(b.data_bolletta) >= last12MonthsStart
    );

    // Last 6 months
    const last6Months = bollette.filter(b => 
      new Date(b.data_bolletta) >= last6MonthsStart
    );

    // Previous 6 months (for trend calculation)
    const prev6Months = bollette.filter(b => {
      const date = new Date(b.data_bolletta);
      return date >= prev6MonthsStart && date <= prev6MonthsEnd;
    });

    // Totals: somma importi grezzi (non normalizzati) per totale spesa reale
    const total12 = last12Months.reduce((sum, b) => sum + parseFloat(b.importo), 0);
    
    const total6Last = last6Months.reduce((sum, b) => sum + parseFloat(b.importo), 0);
    
    const total6Prev = prev6Months.reduce((sum, b) => sum + parseFloat(b.importo), 0);

    // Average monthly: spesa totale / mesi coperti effettivi
    // Somma i periodo_mesi di ogni bolletta per ottenere i mesi totali coperti
    const mesiCoperti12 = last12Months.reduce((sum, b) => sum + (b.periodo_mesi || 1), 0);
    const avgMonthly = mesiCoperti12 > 0 
      ? total12 / mesiCoperti12
      : 0;

    // Trend % (last 6 months vs previous 6 months)
    const trend = total6Prev > 0 
      ? ((total6Last - total6Prev) / total6Prev) * 100 
      : 0;

    return {
      total12,
      avgMonthly,
      trend
    };
  }, [bollette]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return '#d32f2f'; // Crescita = rosso (male)
    if (trend < 0) return '#388e3c'; // Decrescita = verde (bene)
    return '#666';
  };

  const getTrendIcon = (trend) => {
    if (trend > 0) return '📈';
    if (trend < 0) return '📉';
    return '➡️';
  };

  const cards = [
    {
      title: 'Spesa Totale (12 mesi)',
      value: formatCurrency(stats.total12),
      icon: '💰',
      color: '#1976d2'
    },
    {
      title: 'Media Mensile',
      value: formatCurrency(stats.avgMonthly),
      icon: '📊',
      color: '#7b1fa2'
    },
    {
      title: 'Trend (vs 6 mesi precedenti)',
      value: `${stats.trend > 0 ? '+' : ''}${stats.trend.toFixed(1)}%`,
      icon: getTrendIcon(stats.trend),
      color: getTrendColor(stats.trend)
    }
  ];

  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    }}>
      {cards.map((card, index) => (
        <div 
          key={index}
          style={{
            background: '#fff',
            padding: '20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: `4px solid ${card.color}`
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '10px'
          }}>
            <span style={{ 
              fontSize: '13px', 
              color: '#666',
              fontWeight: '500'
            }}>
              {card.title}
            </span>
            <span style={{ fontSize: '24px' }}>{card.icon}</span>
          </div>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: '700',
            color: card.color
          }}>
            {card.value}
          </div>
        </div>
      ))}
    </div>
  );
}

export default StatsCards;
